export class PrimeNgAccessibilityHelper {
  private static observer: MutationObserver | null = null;

  public static initGlobalPatch(): void {
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver(() => {
      // =================================================================
      // 1. FIX: Paginator Elements
      // =================================================================

      // Target paginator dropdown wrappers
      const paginatorDropdowns = document.querySelectorAll(
        '.p-paginator .p-dropdown',
      );
      paginatorDropdowns.forEach((el: Element) => {
        if (!el.hasAttribute('aria-label')) {
          // Providing a combobox role makes aria-label fully supported on this div
          el.setAttribute('role', 'combobox');
          el.setAttribute('aria-expanded', 'false');
          el.setAttribute('aria-label', 'Rows per page');
        }
      });

      // Target paginator trigger icons/containers
      const paginatorTriggers = document.querySelectorAll(
        '.p-paginator .p-dropdown-trigger',
      );
      paginatorTriggers.forEach((el: Element) => {
        if (!el.hasAttribute('aria-label')) {
          // Setting role="button" makes aria-label supported on a structural div
          el.setAttribute('role', 'button');
          el.setAttribute('aria-label', 'Show rows per page options');
        }
      });

      // Fix static links (First, Prev, Next, Last)
      this.setAria('.p-paginator-first', 'First page', 'button');
      this.setAria('.p-paginator-prev', 'Previous page', 'button');
      this.setAria('.p-paginator-next', 'Next page', 'button');
      this.setAria('.p-paginator-last', 'Last page', 'button');

      // Fix numbered page buttons
      const pageButtons = document.querySelectorAll('.p-paginator-page');
      pageButtons.forEach((btn: Element) => {
        if (!btn.hasAttribute('aria-label')) {
          const pageNum = btn.textContent?.trim() || '';
          btn.setAttribute('role', 'button');
          btn.setAttribute('aria-label', `Page ${pageNum}`);
        }
      });

      // =================================================================
      // 2. FIX: Standalone Dropdowns
      // =================================================================

      const prohibitedDropdownInputs = document.querySelectorAll(
        'p-dropdown input[aria-expanded]',
      );
      prohibitedDropdownInputs.forEach((input: Element) => {
        input.removeAttribute('aria-expanded');
      });

      // Standalone Dropdown Trigger containers
      const standaloneDropdownTriggers = document.querySelectorAll(
        'p-dropdown:not(.p-paginator p-dropdown) .p-dropdown-trigger',
      );
      standaloneDropdownTriggers.forEach((trigger: Element) => {
        if (!trigger.hasAttribute('aria-label')) {
          const parentDropdown = trigger.closest('p-dropdown');
          const placeholder =
            parentDropdown
              ?.querySelector('.p-dropdown-label.p-placeholder')
              ?.textContent?.trim() || 'Select option';

          trigger.setAttribute('role', 'button'); // Explicitly support aria-label
          trigger.setAttribute('aria-label', `${placeholder} dropdown trigger`);
        }
      });

      // Internal focusable input element (This NEVER throws prohibited errors because it is an <input>)
      const dropdownInputs = document.querySelectorAll(
        'p-dropdown .p-hidden-accessible input',
      );
      dropdownInputs.forEach((input: Element) => {
        if (
          !input.hasAttribute('aria-label') &&
          !input.hasAttribute('aria-labelledby')
        ) {
          const parentDropdown = input.closest('p-dropdown');
          const placeholder =
            parentDropdown
              ?.querySelector('.p-dropdown-label')
              ?.textContent?.trim() || 'Options menu';

          input.setAttribute('aria-label', placeholder);
        }
      });

      // =================================================================
      // 3. FIX: Checkbox Labels (<p-checkbox>)
      // =================================================================
      const checkboxes = document.querySelectorAll('p-checkbox');
      checkboxes.forEach((cb: Element, index: number) => {
        const innerInput = cb.querySelector('input[type="checkbox"]');
        if (
          innerInput &&
          !innerInput.hasAttribute('aria-label') &&
          !innerInput.hasAttribute('aria-labelledby')
        ) {
          // Look backward in the DOM to try and find an adjacent label element
          const nearbyLabel = cb
            .closest('.p-grid, .row, .p-formgrid')
            ?.querySelector('label');
          const labelText =
            nearbyLabel?.textContent?.trim() || 'Required setting';

          innerInput.setAttribute('aria-label', `Checkbox for ${labelText}`);
        }
      });

      // =================================================================
      // 4. NEW: Textbox Structural Labels ([pInputText])
      // =================================================================
      const textboxes = document.querySelectorAll(
        'input[pInputText], .p-inputtext:not(input)',
      );
      textboxes.forEach((input: Element) => {
        const hasId = input.hasAttribute('id');
        const hasLabel =
          hasId &&
          document.querySelector(`label[for="${input.getAttribute('id')}"]`);
        const hasAriaLabel =
          input.hasAttribute('aria-label') ||
          input.hasAttribute('aria-labelledby');

        if (!hasLabel && !hasAriaLabel) {
          // Trace up to the shared row flexbox container, then look down into the text label container
          const parentRow =
            input.closest('.p-col-fixed')?.parentElement ||
            input.closest('div');
          const siblingLabel = parentRow?.querySelector('.p-col label, label');

          const labelText =
            siblingLabel?.textContent?.trim() ||
            input.getAttribute('placeholder') ||
            'Question response';
          input.setAttribute('aria-label', labelText);
        }
      });

      // =================================================================
      // 5. FIX: Prohibited Span Elements (Icon / Status Fixes)
      // =================================================================

      // Find any span element that was mistakenly assigned an aria-label
      const flaggedSpans = document.querySelectorAll('span[aria-label]');
      flaggedSpans.forEach((span: Element) => {
        // Check if it already has a valid interactive role
        if (!span.hasAttribute('role')) {
          // If it contains a PrimeNG icon class (e.g., .pi-sort, .pi-chevron-down)
          if (
            span.className.includes('pi-') ||
            span.className.includes('p-dropdown-trigger-icon')
          ) {
            // role="img" legitimizes the aria-label for assistive technologies
            span.setAttribute('role', 'img');
          } else {
            // Fallback to role="button" if the span is an active clickable link element
            span.setAttribute('role', 'button');
          }
        }
      });

      // =================================================================
      // 6. FIXED: Anti-Overwrite Lock for PrimeNG Buttons
      // =================================================================
      const allButtons = document.querySelectorAll(
        'button[pButton], button.p-button, p-button button',
      );

      allButtons.forEach((btn: Element) => {
        // 1. CRITICAL PROTECTION: Skip elements already locked or using our custom brand classes
        if (
          btn.hasAttribute('data-a11y-locked') ||
          btn.classList.contains('p-button-custom-teal') ||
          btn.classList.contains('accessible-link')
        ) {
          return;
        }

        const processButton = () => {
          // Check for native label, compiled label attribute, or dynamic property reflections
          const hasStaticLabel =
            (btn.hasAttribute('label') && btn.getAttribute('label') !== '') ||
            btn.hasAttribute('ng-reflect-label');

          const primeTextNode = btn.querySelector('.p-button-label');

          // Filter out standard empty non-breaking space characters (&nbsp;) generated by PrimeNG
          const rawTextContent =
            primeTextNode?.textContent || btn.textContent || '';
          const internalTextContent = rawTextContent
            .replace(/\u00a0/g, ' ')
            .trim();

          // ROUTE A: Button contains genuine visual text (e.g., "Create Form" or "Yes")
          if (hasStaticLabel || internalTextContent.length > 0) {
            // Clean up duplicate labeling loops that trigger Siteimprove warnings
            if (btn.hasAttribute('aria-label')) {
              btn.removeAttribute('aria-label');
            }
            btn.setAttribute('data-a11y-locked', 'true'); // Secure the layout state
            return;
          }

          // ROUTE B: Button is genuinely Icon-Only (e.g., "Copy Form")
          const tooltipText = (
            btn.getAttribute('pTooltip') ||
            btn.getAttribute('ng-reflect-text') ||
            ''
          ).trim();
          const templateAria = (
            btn.getAttribute('aria-label') ||
            btn.getAttribute('ng-reflect-aria-label') ||
            ''
          ).trim();

          // Force the correct label string based on context details
          let finalTargetLabel = templateAria;
          if (tooltipText && tooltipText !== 'undefined') {
            finalTargetLabel = tooltipText;
          } else if (
            !templateAria ||
            templateAria === 'undefined' ||
            templateAria === 'Action button for grid-item'
          ) {
            const parentRow = btn.closest('tr');
            const titleCellText = parentRow
              ?.querySelector('td')
              ?.textContent?.trim();
            const rowContext =
              titleCellText || parentRow?.getAttribute('id') || 'grid-item';
            finalTargetLabel = `Action button for ${rowContext}`;
          }

          // Force apply the correct unique label and lock the attribute
          btn.setAttribute('aria-label', finalTargetLabel);
          btn.setAttribute('data-a11y-locked', 'true');

          // Mute inner icons and empty spaces that confuse Siteimprove
          btn
            .querySelectorAll('.p-button-icon, .pi, .p-button-label')
            .forEach((node: Element) => {
              if (
                node.classList.contains('p-button-label') &&
                internalTextContent.length === 0
              ) {
                node.setAttribute('aria-hidden', 'true');
              } else if (!node.classList.contains('p-button-label')) {
                node.setAttribute('aria-hidden', 'true');
              }
            });
        };

        // Execution Step 1: Run immediately during DOM injection
        processButton();

        // Execution Step 2: Run 100ms later to handle late PrimeNG lifecycle modifications safely
        setTimeout(() => {
          if (btn && !btn.classList.contains('p-button-custom-teal')) {
            btn.removeAttribute('data-a11y-locked'); // Temporarily unlock to re-verify normal items
            processButton();
          }
        }, 100);
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Refactored helper method to cleanly apply labels alongside supported semantic roles
  private static setAria(
    selector: string,
    label: string,
    roleType?: string,
  ): void {
    const elements = document.querySelectorAll(selector);
    elements.forEach((el: Element) => {
      if (roleType && !el.hasAttribute('role')) {
        el.setAttribute('role', roleType);
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', label);
      }
    });
  }
}
